import React from 'react';
import { Link } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';

const insightsData = [
  {
    id: '2026-enterprise-tax',
    category: 'Tax Strategy',
    title: '2026 Enterprise Tax Strategy Guide',
    desc: 'An authoritative whitepaper detailing entity restructuring and SALT compliance for the upcoming fiscal year.',
    date: 'June 2026'
  },
  {
    id: 'ma-due-diligence',
    category: 'Advisory',
    title: 'M&A Due Diligence Framework',
    desc: 'A comprehensive playbook for identifying financial risks and uncovering hidden liabilities during acquisitions.',
    date: 'May 2026'
  },
  {
    id: 'soc-readiness',
    category: 'Risk Management',
    title: 'SOC 2 Readiness Toolkit',
    desc: 'Essential checklists and governance templates to prepare your tech infrastructure for strict compliance audits.',
    date: 'April 2026'
  }
];

const CF_WORKER_URL = 'https://irs-insights-worker.jessjiayizhu.workers.dev/';

function Insights() {
  const [insights, setInsights] = React.useState(insightsData);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Attempt to fetch live data from the Cloudflare Worker
    fetch(CF_WORKER_URL)
      .then(res => {
        if (!res.ok) throw new Error('API fetch failed');
        return res.json();
      })
      .then(data => {
        if (data && data.length > 0) {
          setInsights(data);
        }
        setLoading(false);
      })
      .catch(err => {
        console.log('Using fallback local insights data:', err.message);
        setInsights(insightsData); // Fallback to local data
        setLoading(false);
      });
  }, []);

  return (
    <PageTransition>
      {/* Hero header */}
      <div style={{
        background: 'linear-gradient(135deg, var(--clr-navy) 0%, var(--clr-navy-deep) 100%)',
        padding: '100px 24px 80px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
          fontWeight: 700,
          color: 'var(--clr-white)',
          lineHeight: 1.15,
          marginBottom: 24,
        }}>Thought Leadership</h1>
        <p style={{
          fontSize: '1.1rem', color: 'rgba(255,255,255,0.75)',
          maxWidth: 600, margin: '0 auto', lineHeight: 1.6
        }}>
          In-depth financial analysis, regulatory whitepapers, and strategic frameworks engineered for enterprise growth.
        </p>
      </div>

      <section className="byhw-section" style={{paddingTop: 80, paddingBottom: 100, background: 'var(--clr-offwhite)'}}>
        <div style={{maxWidth: 1200, margin: '0 auto'}}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 40
          }}>
            {loading ? (
              <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--clr-gray)'}}>
                Loading latest insights...
              </div>
            ) : insights.map((item, i) => (
              <ScrollReveal key={item.id} delay={i * 0.1}>
                <div style={{
                  background: 'var(--clr-white)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  border: '1px solid var(--clr-border)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    height: 160,
                    background: 'var(--clr-navy)',
                    padding: 32,
                    display: 'flex',
                    alignItems: 'flex-end',
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute', top: 24, right: 24,
                      background: 'rgba(255,255,255,0.1)',
                      color: 'var(--clr-white)', fontSize: '0.75rem', fontWeight: 600,
                      padding: '4px 12px', borderRadius: 20, letterSpacing: '0.05em', textTransform: 'uppercase'
                    }}>
                      {item.category}
                    </div>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--clr-accent-gold)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{opacity: 0.8}}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  </div>
                  <div style={{padding: 32, flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
                    <div style={{fontSize: '0.85rem', color: 'var(--clr-gray-light)', marginBottom: 12, fontWeight: 500}}>{item.date}</div>
                    <h3 style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: '1.3rem',
                      color: 'var(--clr-navy)',
                      marginBottom: 16,
                      lineHeight: 1.4
                    }}>{item.title}</h3>
                    <p style={{
                      color: 'var(--clr-text-light)',
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                      marginBottom: 32,
                      flexGrow: 1
                    }}>{item.desc}</p>
                    
                    <a 
                      href={item.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--clr-primary)',
                        color: 'var(--clr-primary)',
                        padding: '12px 24px',
                        borderRadius: 6,
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        transition: 'all 0.2s ease',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--clr-primary)'; e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--clr-primary)'; }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                      Read Full Article
                    </a>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* Call to action */}
      <section style={{padding: '80px 24px', textAlign: 'center', background: 'var(--clr-white)'}}>
        <h2 style={{fontFamily: "var(--font-heading)", fontSize: '2rem', color: 'var(--clr-navy)', marginBottom: 24}}>Need Customized Strategic Advice?</h2>
        <p style={{color: 'var(--clr-text-light)', maxWidth: 600, margin: '0 auto 40px', fontSize: '1.05rem', lineHeight: 1.6}}>
          Our partners are available to discuss how these insights apply to your specific organizational context.
        </p>
        <Link to="/contact" className="byhw-cta">Request Executive Consultation</Link>
      </section>
    </PageTransition>
  );
}

export default Insights;
